import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import axios from 'axios';
import SHORTLIST from '../voiceShortlist.json';
import './VoiceSection.css';

const API = process.env.REACT_APP_API_URL || '';
const PANEL_KEY = 'timbo_voice_panel';   // ids on the Studio panel (the user's shortlist)

// Cached sample of a voice (backend/voices.js): generated once per (model, voice), then streamed.
// Falls back to ElevenLabs' own preview_url if the endpoint fails.
async function previewUrlFor(voice, apiKey) {
  try {
    const r = await axios.post(`${API}/api/voices/preview`, { voiceId: voice.id, apiKey: apiKey || undefined }, { timeout: 60000 });
    return `${API}/api/stream/${r.data.filename}`;
  } catch (e) {
    if (voice.preview_url) return voice.preview_url;
    throw e;
  }
}

function loadPanel() {
  try {
    const v = JSON.parse(localStorage.getItem(PANEL_KEY));
    if (Array.isArray(v)) return v;
  } catch {}
  return null;   // never customised → default to voiceShortlist.json
}
function savePanel(ids) { try { localStorage.setItem(PANEL_KEY, JSON.stringify(ids)); } catch {} }

const genderOf = v => {
  const g = (v.gender || v.labels?.gender || '').toLowerCase();
  if (g === 'female' || g === 'male') return g;
  const c = (v.category || '').toLowerCase();
  return c.includes('female') ? 'female' : c.includes('male') ? 'male' : '';
};
// One-line register: our curated line for shortlist voices, else what ElevenLabs says about the voice.
const registerOf = v => {
  const sl = SHORTLIST.voices.find(s => s.id === v.id);
  if (sl) return sl.register;
  return [v.description, v.accent].filter(Boolean).join(' · ');
};

// ▶ preview with a single shared <audio>; returns { state, toggle }.
function usePreview(findVoice, elApiKey) {
  const [state, setState] = useState({ playing: false, generating: false, voiceId: null });
  const audioRef = useRef(null);
  const stop = () => { if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; } };
  const idle = () => setState({ generating: false, playing: false, voiceId: null });
  useEffect(() => stop, []);
  const toggle = useCallback(async (voiceId) => {
    if (state.playing && state.voiceId === voiceId) { stop(); idle(); return; }
    stop();
    if (state.generating) return;
    const key = (elApiKey || localStorage.getItem('el_api_key') || '').trim();
    setState({ generating: true, playing: false, voiceId });
    try {
      const url = await previewUrlFor(findVoice(voiceId) || { id: voiceId }, key);
      const a = new Audio(url); audioRef.current = a;
      a.onended = idle; a.onerror = idle;
      await a.play();
      setState({ generating: false, playing: true, voiceId });
    } catch { idle(); }
  }, [elApiKey, state, findVoice]);
  return { state, toggle };
}

function PreviewBtn({ voiceId, preview }) {
  const on = preview.state.voiceId === voiceId;
  return (
    <button type="button"
      className={`voice-preview-btn ${on && preview.state.playing ? 'playing' : ''} ${on && preview.state.generating ? 'generating' : ''}`}
      onClick={e => { e.stopPropagation(); preview.toggle(voiceId); }}
      title={on && preview.state.generating ? 'Generating…' : 'Preview'}>
      {on && preview.state.generating ? '⏳' : on && preview.state.playing ? '⏹' : '▶'}
    </button>
  );
}

// ── "Choose voices" modal — the only place that lists the whole account ──────
function ChooseVoicesModal({ onClose, accountVoices, syncStatus, onSync, panelIds, setPanelIds, missingIds, preview }) {
  const [q, setQ] = useState('');
  const [gender, setGender] = useState('all');
  const query = q.trim().toLowerCase();
  const list = accountVoices.filter(v =>
    (gender === 'all' || genderOf(v) === gender) &&
    (!query || (v.label || '').toLowerCase().includes(query) || registerOf(v).toLowerCase().includes(query)));
  const toggle = id => setPanelIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const onPanel = panelIds.filter(id => accountVoices.some(v => v.id === id)).length;

  return (
    <div className="vc-backdrop" onClick={onClose}>
      <div className="vc-box" onClick={e => e.stopPropagation()}>
        <div className="vc-head">
          <h3>Choose voices</h3>
          <button className="vc-x" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="vc-body">
          {missingIds.length > 0 && (
            <div className="vc-missing">
              {missingIds.length === 1 ? 'A voice on your panel is' : `${missingIds.length} voices on your panel are`} no longer on the ElevenLabs account:
              {missingIds.map(id => (
                <span key={id} className="vc-missing-item">
                  {SHORTLIST.voices.find(s => s.id === id)?.name || id}
                  <button onClick={() => setPanelIds(prev => prev.filter(x => x !== id))}>Remove</button>
                </span>
              ))}
            </div>
          )}
          {accountVoices.length === 0 ? (
            <div className="vc-empty">
              {syncStatus === 'loading' ? 'Loading voices…'
                : syncStatus === 'nokey' ? 'Add your ElevenLabs API key in Settings to list your voices.'
                : 'Couldn’t load voices from ElevenLabs.'}
              {syncStatus !== 'loading' && syncStatus !== 'nokey' && <button className="vc-btn" onClick={onSync}>↻ Retry</button>}
            </div>
          ) : (
            <>
              <div className="vc-filters">
                <input className="vc-search" placeholder="Search by name or style…" value={q} onChange={e => setQ(e.target.value)} autoFocus />
                <span className="vc-seg">
                  {[['all', 'All'], ['female', 'Female'], ['male', 'Male']].map(([k, l]) => (
                    <button key={k} className={gender === k ? 'on' : ''} onClick={() => setGender(k)}>{l}</button>
                  ))}
                </span>
              </div>
              <div className="vc-list">
                {list.map(v => {
                  const on = panelIds.includes(v.id);
                  return (
                    <div key={v.id} className={`vc-row ${on ? 'on' : ''}`} onClick={() => toggle(v.id)}>
                      <span className="vc-star">{on ? '★' : '☆'}</span>
                      <span className="vc-txt"><span className="vc-n">{v.label}</span><span className="vc-d">{registerOf(v)}</span></span>
                      <PreviewBtn voiceId={v.id} preview={preview} />
                    </div>
                  );
                })}
                {list.length === 0 && <div className="vc-empty">No voice matches.</div>}
              </div>
            </>
          )}
        </div>
        <div className="vc-foot">
          <span><b>{onPanel}</b> voice{onPanel === 1 ? '' : 's'} on your panel · {accountVoices.length} available on the account</span>
          <button className="vc-btn vc-pri" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}

// ── VoiceSection ──────────────────────────────────────────────────────────────
export default function VoiceSection({
  selectedVoiceIds, setSelectedVoiceIds,
  soundEnabled, setSoundEnabled, scriptEnabled,
  elApiKey, t,
}) {
  const [accountVoices, setAccountVoices] = useState([]);   // exactly what the account returned
  const [syncStatus, setSyncStatus]       = useState('idle');
  const [showModal, setShowModal]         = useState(false);
  const [panelIds, setPanelIdsState]      = useState(loadPanel);
  const lastKeyRef = useRef('');

  const setPanelIds = useCallback(upd => setPanelIdsState(prev => {
    const base = prev || SHORTLIST.voices.map(s => s.id);
    const next = typeof upd === 'function' ? upd(base) : upd;
    savePanel(next);
    return next;
  }), []);

  const doSync = useCallback((forceKey) => {
    const key = (forceKey || elApiKey || localStorage.getItem('el_api_key') || '').trim();
    if (!key) { setSyncStatus('nokey'); return; }
    if (key === lastKeyRef.current && accountVoices.length > 0 && !forceKey) return;
    setSyncStatus('loading');
    axios.get(`${API}/api/voices/sync?apiKey=${encodeURIComponent(key)}`, { timeout: 12000 })
      .then(r => {
        if (Array.isArray(r.data) && r.data.length > 0) { setAccountVoices(r.data); setSyncStatus('ok'); lastKeyRef.current = key; }
        else setSyncStatus('error');
      })
      .catch(() => setSyncStatus('error'));
  }, [elApiKey, accountVoices.length]);
  useEffect(() => { doSync(); }, [elApiKey]); // eslint-disable-line

  const accountIds = useMemo(() => new Set(accountVoices.map(v => v.id)), [accountVoices]);
  const effectivePanel = panelIds || SHORTLIST.voices.map(s => s.id);
  // Before the first sync we can't tell what's on the account: show nothing rather than guess.
  const synced = accountVoices.length > 0;
  const panelVoices = synced ? effectivePanel.map(id => accountVoices.find(v => v.id === id)).filter(Boolean) : [];
  const missingIds  = synced ? effectivePanel.filter(id => !accountIds.has(id)) : [];

  // Keep the selection valid: only panel voices, never empty.
  useEffect(() => {
    if (!synced) return;
    const ok = selectedVoiceIds.filter(id => panelVoices.some(v => v.id === id));
    if (ok.length === selectedVoiceIds.length && ok.length > 0) return;
    if (ok.length > 0) setSelectedVoiceIds(ok);
    else if (panelVoices.length > 0) setSelectedVoiceIds([panelVoices[0].id]);
  }, [synced, panelIds, accountVoices]); // eslint-disable-line

  const findVoice = useCallback(id => accountVoices.find(v => v.id === id), [accountVoices]);
  const preview = usePreview(findVoice, elApiKey);

  const toggleVoice = id => {
    if (!soundEnabled) return;
    setSelectedVoiceIds(p => p.includes(id) ? (p.length > 1 ? p.filter(v => v !== id) : p) : [...p, id]);
  };

  const muted = !(soundEnabled && scriptEnabled);

  return (
    <>
      {showModal && (
        <ChooseVoicesModal
          onClose={() => setShowModal(false)}
          accountVoices={accountVoices} syncStatus={syncStatus}
          onSync={() => { lastKeyRef.current = ''; doSync(elApiKey || localStorage.getItem('el_api_key') || ''); }}
          panelIds={effectivePanel} setPanelIds={setPanelIds} missingIds={missingIds} preview={preview}
        />
      )}

      <div className="control-section">
        <div className="section-title-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span className="section-title" style={{ marginBottom: 0 }}>
            {t ? t('studio_voice') : 'Voice'}{' '}
            <span style={{ fontSize: 10, color: 'var(--text-dim)', fontWeight: 400 }}>{muted ? 'off' : 'on'}</span>
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button className="vc-btn" onClick={() => setShowModal(true)}>Choose voices</button>
            <button disabled={!scriptEnabled}
              className={`pill-toggle ${!muted ? 'pill-on' : 'pill-off'}`}
              onClick={() => setSoundEnabled(v => !v)}>
              <span className="pill-track"><span className="pill-thumb" /></span>
            </button>
          </div>
        </div>

        <div className={muted ? 'voice-section-muted' : ''}>
          {panelVoices.length > 0 ? (
            <div className="vp-grid">
              {panelVoices.map(v => {
                const active = selectedVoiceIds.includes(v.id);
                const g = genderOf(v);
                return (
                  <div key={v.id} className={`vp-card ${active ? 'on' : ''}`} onClick={() => toggleVoice(v.id)}>
                    {g && <span className={`vp-g ${g}`}>{g === 'female' ? 'F' : 'M'}</span>}
                    <span className="vp-txt"><span className="vp-n">{v.label}</span><span className="vp-d">{registerOf(v)}</span></span>
                    <PreviewBtn voiceId={v.id} preview={preview} />
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="vp-empty">
              {syncStatus === 'loading' ? 'Loading voices…'
                : syncStatus === 'nokey' ? 'Add your ElevenLabs API key in Settings to load your voices.'
                : syncStatus === 'error' ? 'Couldn’t load voices from ElevenLabs.'
                : 'No voice on your panel yet — use “Choose voices” to pick the ones you use.'}
            </div>
          )}
          {missingIds.length > 0 && (
            <div className="vp-warn">{missingIds.length === 1 ? '1 voice' : `${missingIds.length} voices`} on your panel left the account — open “Choose voices” to remove {missingIds.length === 1 ? 'it' : 'them'}.</div>
          )}
        </div>
      </div>
    </>
  );
}
